import React from "react";
import { LegalLayout, Section } from "@/components/legal/LegalLayout";

const P: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[color:var(--sg-fg)] leading-relaxed">{children}</p>
);

export const DataDeletion: React.FC = () => (
  <LegalLayout
    testId="page-data-deletion"
    title="Data Deletion Instructions"
    subtitle="How to delete your SociBaba data — required for Meta App Review and available to all users."
    lastUpdated="February 13, 2026"
    seoPath="/data-deletion"
    seoDescription="Self-service and email-based instructions to delete your SociBaba workspace, media, OAuth tokens and Discovery reports."
    seoKeywords={["delete SociBaba account","GDPR right to erasure","Meta data deletion","account deletion"]}
  >
    <P>
      You have the right to delete your SociBaba account and all associated data at any time.
    </P>

    <Section n={1} title="Self-service (recommended)">
      <ol className="list-decimal ml-6 space-y-2">
        <li>Sign in at <a className="text-[color:var(--sg-primary)] hover:underline" href="/login">https://www.socibaba.com/login</a>.</li>
        <li>Go to <b>Settings</b> in the sidebar.</li>
        <li>Click <b>Delete workspace</b> and confirm. Your account and all workspace content (products, media, publish jobs, comments, leads, complaints, Discovery reports, watchlists, connected platform tokens) will be permanently deleted within 30 days.</li>
      </ol>
    </Section>

    <Section n={2} title="Email request">
      <P>
        If you can't sign in, email <a className="text-[color:var(--sg-primary)] hover:underline" href="mailto:privacy@socibaba.com">privacy@socibaba.com</a> from the email address associated with your SociBaba account. Include:
      </P>
      <ul className="list-disc ml-6 mt-2 space-y-1">
        <li>Your account email</li>
        <li>Workspace name (if you know it)</li>
        <li>The subject line: "Data Deletion Request"</li>
      </ul>
      <P className="mt-2">We will confirm deletion within 30 days.</P>
    </Section>

    <Section n={3} title="Meta / Facebook data deletion callback">
      <P>
        Users who signed in via Facebook can also request deletion of their Facebook-derived data by visiting <a className="text-[color:var(--sg-primary)] hover:underline" href="https://www.facebook.com/help/contact/540977946302970" target="_blank" rel="noreferrer">Facebook's Data Deletion form</a> and including your SociBaba account email. We will process it within 30 days.
      </P>
    </Section>

    <Section n={4} title="What gets deleted">
      <ul className="list-disc ml-6 space-y-1">
        <li>Your user profile, credentials, and session tokens</li>
        <li>Workspace records and all associated products, media metadata, thumbnails, publish jobs</li>
        <li>Comments, leads, complaints, and Discovery reports linked to your workspace</li>
        <li>OAuth tokens for any connected YouTube/Facebook/Instagram accounts (SociBaba will no longer be able to act on your behalf)</li>
        <li>Object-storage media associated with your workspace</li>
      </ul>
    </Section>

    <Section n={5} title="What may be retained">
      <P>
        We may retain minimal transaction records and server logs for up to 90 days for legal, tax, and security purposes. These records do not contain your content and are securely destroyed after the retention period.
      </P>
    </Section>
  </LegalLayout>
);

export default DataDeletion;
